import { Text, Skeleton } from '@mantine/core'
import { type ReactQuery } from 'cms-api'
import Link from 'next/link'
import React from 'react'
import { formatPrice, ROUTE } from 'shared-constants'
import styled, { keyframes } from 'styled-components'
import { Button, BUTTON_VARIANT, BUTTON_ICON_POSITION } from 'components/Button'
import { Card, CARD_ELEVATION, CARD_PADDING_SIZE } from 'components/Card'
import Icon from 'components/Icon/Icon'
import { Box, Flex, Grid } from 'components/Layout'
import { COLOR_NAMES, SPACE } from 'Theme'
import { TIER_LADDER, fmtThresholdK, type Tier } from '../tierLadder'

const GREEN = COLOR_NAMES.PINEY_LAKE
const INK = COLOR_NAMES.EEIRE_BLACK
const MUTED = COLOR_NAMES.GRAY_SHINGLE
const LINE = COLOR_NAMES.AMERICAN_SILVER

const DOT_SIZE = 36

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(18, 101, 84, 0.28); }
  70% { box-shadow: 0 0 0 12px rgba(18, 101, 84, 0); }
  100% { box-shadow: 0 0 0 0 rgba(18, 101, 84, 0); }
`

const CurrentNode = styled.div`
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid ${GREEN};
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulseRing} 2s ease-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    box-shadow: 0 0 0 6px rgba(18, 101, 84, 0.1);
  }
`

type TierJourneyWidgetProps = {
  currentTier?: Tier
  currentYearSpend?: number
  spendToNextTier: ReactQuery.ExpertDashboardTradeTierResponse['spendToNextTier']
  isLoading?: boolean
}

export const TierJourneyWidget = ({
  currentTier,
  currentYearSpend = 0,
  spendToNextTier,
  isLoading,
}: TierJourneyWidgetProps) => {
  const year = new Date().getFullYear()
  const ladderLength = TIER_LADDER.length

  const rawIdx = TIER_LADDER.findIndex((t) => t.name === currentTier)
  const currentIdx = rawIdx < 0 ? 0 : rawIdx
  const current = TIER_LADDER[currentIdx]
  const next = TIER_LADDER[currentIdx + 1] ?? null

  const progress = next
    ? Math.min(
        1,
        Math.max(
          0,
          (currentYearSpend - current.threshold) /
            (next.threshold - current.threshold)
        )
      )
    : 1
  const fillFraction = (currentIdx + progress) / (ladderLength - 1)

  // Rail spans from the centre of the first node to the centre of the last node
  const sideInset = 100 / (2 * ladderLength)
  const railSpan = 100 - 100 / ladderLength

  if (isLoading) {
    return (
      <Card
        flexDirection="column"
        width="100%"
        elevation={CARD_ELEVATION.NONE}
        hasBorders
        paddingSize={CARD_PADDING_SIZE.NONE}
      >
        <Box p={SPACE.PX_30} width="100%">
          <Skeleton height={22} width="40%" mb={10} />
          <Skeleton height={36} width="55%" mb={10} />
          <Skeleton height={14} width="60%" mb={30} />
          <Skeleton height={100} radius="md" />
        </Box>
      </Card>
    )
  }

  return (
    <Card
      flexDirection="column"
      width="100%"
      elevation={CARD_ELEVATION.NONE}
      hasBorders
      paddingSize={CARD_PADDING_SIZE.NONE}
    >
      <Box p={{ MOBILE: SPACE.PX_16, TABLET: SPACE.PX_30 }} width="100%">
        {/* Header */}
        <Text
          tt="uppercase"
          fz={11}
          fw={600}
          c={MUTED}
          style={{ letterSpacing: '0.14em' }}
        >
          Trade Perks
        </Text>
        <Text
          fz={{ base: 24, sm: 32 }}
          fw={500}
          c={INK}
          mt={SPACE.PX_4}
          style={{ lineHeight: 1.1 }}
        >
          You&apos;re{' '}
          <Text
            component="span"
            fz={{ base: 24, sm: 32 }}
            fw={500}
            fs="italic"
            c={INK}
          >
            {current.name}
          </Text>{' '}
          in {year}.
        </Text>
        <Flex
          mt={SPACE.PX_8}
          gap={SPACE.PX_8}
          alignItems="baseline"
          flexWrap="wrap"
        >
          <Text
            tt="uppercase"
            fz={12}
            fw={600}
            c={MUTED}
            style={{ letterSpacing: '0.12em' }}
          >
            YTD Spend
          </Text>
          <Text fz={15} fw={600} c={INK}>
            {formatPrice({ price: currentYearSpend, roundToInteger: true })}
          </Text>
          {next && (
            <>
              <Text fz={14} c={MUTED}>
                ·
              </Text>
              <Text fz={14} c={MUTED}>
                Keep shopping to unlock your firm&apos;s next Perks tier
              </Text>
            </>
          )}
        </Flex>

        {/* Stepper */}
        <Box mt={SPACE.PX_30}>
          {/* Threshold labels */}
          <Grid gridTemplateColumns={`repeat(${ladderLength}, 1fr)`}>
            {TIER_LADDER.map((t) => (
              <Text
                key={t.name}
                tt="uppercase"
                fz={11}
                c={MUTED}
                ta="center"
                style={{ letterSpacing: '0.08em' }}
              >
                {fmtThresholdK(t.threshold)}
              </Text>
            ))}
          </Grid>

          {/* Dots + rail */}
          <Box
            position="relative"
            my={SPACE.PX_12}
            style={{ height: DOT_SIZE }}
          >
            <Box
              position="absolute"
              style={{
                left: `${sideInset}%`,
                right: `${sideInset}%`,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 2,
                background: LINE,
              }}
            />
            <Box
              position="absolute"
              style={{
                left: `${sideInset}%`,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 2,
                width: `calc(${railSpan}% * ${fillFraction})`,
                background: GREEN,
              }}
            />
            <Grid
              gridTemplateColumns={`repeat(${ladderLength}, 1fr)`}
              position="relative"
              zIndex={1}
              height="100%"
            >
              {TIER_LADDER.map((t, i) => {
                const isDone = i < currentIdx
                const isCurrent = i === currentIdx
                return (
                  <Flex
                    key={t.name}
                    justifyContent="center"
                    alignItems="center"
                  >
                    {isCurrent ? (
                      <CurrentNode>
                        <Box
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: GREEN,
                          }}
                        />
                      </CurrentNode>
                    ) : (
                      <Box
                        style={{
                          width: DOT_SIZE,
                          height: DOT_SIZE,
                          borderRadius: '50%',
                          background: isDone ? GREEN : '#fff',
                          border: isDone
                            ? `2px solid ${GREEN}`
                            : `1.5px dashed ${LINE}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {isDone && <Icon.Checkmark size={18} color="#fff" />}
                      </Box>
                    )}
                  </Flex>
                )
              })}
            </Grid>
          </Box>

          {/* Names + status */}
          <Grid gridTemplateColumns={`repeat(${ladderLength}, 1fr)`}>
            {TIER_LADDER.map((t, i) => {
              const isDone = i < currentIdx
              const isCurrent = i === currentIdx
              const isNext = i === currentIdx + 1
              return (
                <Box key={t.name} style={{ textAlign: 'center' }}>
                  <Text
                    fz={{ base: 12, sm: 20 }}
                    fw={isCurrent ? 600 : 500}
                    c={isDone || isCurrent ? INK : MUTED}
                  >
                    {t.name}
                  </Text>
                  {isCurrent && (
                    <Text
                      tt="uppercase"
                      fz={{ base: 9, sm: 11 }}
                      fw={600}
                      c={GREEN}
                      mt={SPACE.PX_4}
                      style={{ letterSpacing: '0.06em' }}
                    >
                      You are here
                    </Text>
                  )}
                  {isNext && (
                    <Text
                      tt="uppercase"
                      fz={{ base: 9, sm: 11 }}
                      fw={600}
                      c={INK}
                      mt={SPACE.PX_4}
                      style={{ letterSpacing: '0.06em' }}
                    >
                      Up next
                    </Text>
                  )}
                </Box>
              )
            })}
          </Grid>
        </Box>

        {/* Next-tier callout */}
        {next && (
          <Flex
            mt={SPACE.PX_20}
            p={SPACE.PX_16}
            alignItems="center"
            justifyContent="space-between"
            gap={SPACE.PX_16}
            flexWrap="wrap"
            backgroundColor={COLOR_NAMES.TETON_BREEZE}
            borderRadius={SPACE.PX_12}
          >
            <Flex alignItems="center" gap={SPACE.PX_12}>
              <Flex
                alignItems="center"
                justifyContent="center"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  background: '#fff',
                  flex: 'none',
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={GREEN}
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
                  <path d="M20 2v4" />
                  <path d="M22 4h-4" />
                  <circle cx="4" cy="20" r="2" />
                </svg>
              </Flex>
              <Box>
                <Text
                  tt="uppercase"
                  fz={11}
                  fw={600}
                  c={MUTED}
                  style={{ letterSpacing: '0.1em' }}
                >
                  Unlock {next.name}
                </Text>
                <Text fz={15} fw={500} c={INK}>
                  Spend{' '}
                  <Text component="span" fz={15} fw={600} c={INK}>
                    {formatPrice({
                      price: spendToNextTier ?? 0,
                      roundToInteger: true,
                    })}
                  </Text>{' '}
                  more to unlock everything in {next.name}.
                </Text>
              </Box>
            </Flex>
            <Link href={ROUTE.SHOWROOM()}>
              <Button
                variant={BUTTON_VARIANT.FILLED}
                icon={Icon.ArrowRight}
                iconPosition={BUTTON_ICON_POSITION.RIGHT}
              >
                Shop to next tier
              </Button>
            </Link>
          </Flex>
        )}
      </Box>
    </Card>
  )
}
